import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertGenerationAccess } from "./access.server";
import {
  citationFullRules,
  citationInlineRule,
  citationReferenceRule,
  apa7AuthorBlockInstruction,
  APA7_RULES,
} from "./citation-rules";
import { VoiceProfileSchema, styleAndVoiceRules, type VoiceProfile } from "./voice-style";
import { z } from "zod";

const VoiceProfileInput = VoiceProfileSchema.optional().nullable();

// =====================================================================
// STRICT EVIDENCE-CLAIM ALIGNMENT RULE
// Injected into every prompt that pulls or attaches evidence to a claim.
// Keep this wording stable — it is the single source of truth.
// =====================================================================
export const STRICT_EVIDENCE_RULE = `STRICT EVIDENCE–CLAIM ALIGNMENT RULE (HIGHEST PRIORITY):
- You may ONLY return a quote if it DIRECTLY and CLEARLY supports the specific claim it is attached to.
- Before attaching any quote, silently restate the claim in your head, then scan the sources/work for passages that genuinely back THAT claim.
- If a quote is only loosely related, tangential, off-topic, or merely shares a keyword with the claim, DO NOT include it — even if it lives in the provided sources.
- NEVER stretch, reinterpret, paraphrase, or force a quote to fit a claim. Use direct quotes only, word-for-word.
- For every quote you DO return, include a short student-friendly "why_supports" explanation (1–2 sentences) naming exactly how it backs the claim.
- If NO strong, directly-supporting evidence exists for a claim, return zero quotes for that claim and tell the student clearly: no strong match was found, and they should either revise the claim or look for stronger sources.
- It is BETTER to return fewer high-quality, directly relevant quotes than many loosely related ones.
- If unsure whether a quote truly supports the claim, err on the side of NOT including it.
- Never invent, fabricate, or hallucinate quotes, citations, or page numbers.
- Correlation is not causation: if a claim asserts "X causes Y" and the source only shows X and Y co-occur or correlate, that is NOT supporting evidence — exclude it.`;

const InputSchema = z.object({
  assignment_type: z.enum(["essay", "summary", "key_concepts"]),
  title: z.string().min(1).max(300),
  prompt: z.string().max(10000).optional(),
  source_text: z.string().max(40000).optional(),
  evidence_count: z.number().int().min(1).max(10).optional().default(1),
  level: z.enum(["intermediate", "advanced"]).optional().default("intermediate"),
  tone: z.enum(["academic", "natural"]).optional().default("academic"),
  paragraph_count: z.number().int().min(1).max(20).optional().default(4),
  word_min: z.number().int().min(100).max(10000).optional().default(500),
  word_max: z.number().int().min(100).max(10000).optional().default(800),
  voice_profile: VoiceProfileInput,
});

const OutlineSchema = z.object({
  overview: z.string(),
  sections: z.array(
    z.object({
      claim: z.string(),
      evidence: z.array(z.object({ quote: z.string(), citation: z.string() })),
      reasoning_frame: z.string(),
      reasoning_options: z.array(z.string()),
      hint: z.string().default("Use the sentence frame to explain how the evidence supports the claim in your own words."),
    })
  ),
  closing_frame: z.string(),
  closing_options: z.array(z.string()),
});

export const generateOutline = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => InputSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");

    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");

    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const typeDesc = {
      essay: "a structured essay built from Claim-Evidence-Reasoning paragraphs that answers the given prompt",
      summary: "a Claim-Evidence-Reasoning style summary of the work's main ideas",
      key_concepts: "a Claim-Evidence-Reasoning breakdown of the key concepts and themes of the work",
    }[data.assignment_type];

    const sourceClause = data.source_text
      ? `Use ONLY the provided source text below for quotes — do not invent passages.\n\nSOURCE TEXT:\n"""\n${data.source_text}\n"""`
      : `The student did not paste source text. Use well-known, verifiable, widely-quoted passages from "${data.title}". Only include quotes you are highly confident are accurate and word-for-word from the original work. If you are uncertain about exact wording, paraphrase clearly and mark citation as "paraphrased".`;

    const promptLabel = {
      essay: "ESSAY PROMPT THE STUDENT MUST ANSWER",
      summary: "SUMMARY FOCUS / QUESTION THE STUDENT WANTS ANSWERED",
      key_concepts: "KEY CONCEPTS QUESTION THE STUDENT WANTS ANSWERED",
    }[data.assignment_type];
    const promptClause = data.prompt
      ? `\n\n${promptLabel}:\n"""\n${data.prompt}\n"""\nEvery claim must directly serve this prompt.`
      : "";

    const evidenceCount = data.evidence_count;
    const evidenceClause =
      evidenceCount === 1
        ? "Each section must include EXACTLY 1 cited evidence quote that supports the claim."
        : `Each section must include EXACTLY ${evidenceCount} cited evidence quotes that support the claim from different angles.`;
    const evidenceSchema = Array.from({ length: evidenceCount }, () => '{ "quote": "string", "citation": "string" }').join(", ");

    const levelClause =
      data.level === "advanced"
        ? "VOCABULARY LEVEL: Advanced / undergraduate. Use precise academic vocabulary, varied sentence structure, and analytical phrasing appropriate for a first- or second-year college humanities course. Avoid jargon for its own sake, but do not simplify."
        : "VOCABULARY LEVEL: Intermediate (10th-grade equivalent). Keep all writing — claims, reasoning frames, options, and hints — within a 10th-grade reading level. Use clear, common words. Avoid advanced or specialized academic vocabulary. Short, direct sentences.";
    const wmin = Math.min(data.word_min, data.word_max);
    const wmax = Math.max(data.word_min, data.word_max);
    const wordRange = { min: wmin, max: wmax };
    const paragraphCount = data.paragraph_count;
    const perParagraph = Math.round(((wordRange.min + wordRange.max) / 2) / (paragraphCount + 1));
    const toneClause =
      data.tone === "natural"
        ? `VOICE & TONE: Write in a natural, real-student voice — warm but still academic. Mix short punchy sentences with longer flowing ones (high "burstiness"). Use contractions naturally (it's, don't, you're, can't, we've). Use real student transitions like "What's interesting is…", "This part really shows…", "At the same time…", "For example…", "Another thing that stands out…". Vary vocabulary and sentence starters — never repetitive or robotic. Sentence frames and blanks should feel like something the student themselves would start writing. Add very light, natural phrasing real students use when thinking through a text. Smart, not overly formal or perfect.`
        : `VOICE & TONE: Clear, academic, and neutral. Avoid contractions and casual phrasing.`;

    const systemPrompt = `You are SmartEssay, a study coach that builds Claim-Evidence-Reasoning (CER) outlines for intermediate and advanced students. You produce ${typeDesc} for the work titled "${data.title}".

Write clearly and academically. Keep claims, reasoning frames, options, and hints at the appropriate vocabulary level for the student.

${levelClause}

${toneClause}

LENGTH TARGET: The finished student essay should land between ${wordRange.min} and ${wordRange.max} words total, split across ${paragraphCount} body CER paragraphs plus a short closing. Scale the depth of each claim, reasoning frame, and suggested phrases so a student filling in the blanks would naturally hit roughly ${perParagraph} words per paragraph.
LENGTH TARGET: The finished student essay should land between ${wordRange.min} and ${wordRange.max} words total, split across ${paragraphCount} body CER paragraphs plus a short closing. Scale the depth of each claim, reasoning frame, and suggested phrases so a student filling in the blanks would naturally hit roughly ${perParagraph} words per paragraph.

CER format rules:
- CLAIM: one clear sentence stating the point of this paragraph (written for the student).
- EVIDENCE: ${evidenceClause} Each evidence is a short (1–3 sentence) word-for-word quote with a proper citation. Apply the STRICT EVIDENCE–CLAIM ALIGNMENT RULE below to every quote you attach: it must DIRECTLY support THIS section's claim — never stretched, never tangential. If no strong supporting quote exists, return fewer quotes for that section rather than padding with weak matches.
- REASONING: a sentence frame with ___ blanks where the student explains HOW the evidence supports the claim. Never write the reasoning for them. Make the frame long enough to support the per-paragraph word target above.
- For each reasoning frame, also provide 3–4 short suggested phrases (5–12 words each) the student can drop into the blanks. Vary the angles.
- HINT: every section must include one brief student-friendly hint for how to complete the reasoning blank.
- Produce EXACTLY ${paragraphCount} CER sections — no more, no fewer.
- Be honest: if you cannot verify a quote, paraphrase and label the citation "paraphrased".

${STRICT_EVIDENCE_RULE}

${sourceClause}${promptClause}

${styleAndVoiceRules(data.voice_profile as VoiceProfile | null | undefined)}


Respond ONLY with a JSON object matching this exact shape (no markdown, no commentary):
{
  "overview": "string",
  "sections": [
    {
      "claim": "string",
      "evidence": [${evidenceSchema}],
      "reasoning_frame": "string",
      "reasoning_options": ["string", "string", "string"],
      "hint": "string"
    }
  ],
  "closing_frame": "string",
  "closing_options": ["string", "string", "string"]
}`;

    // Fetch prior outputs for this user+title to avoid repetition
    const { data: priors } = await context.supabase
      .from("generations")
      .select("output")
      .eq("user_id", context.userId)
      .eq("title", data.title)
      .eq("assignment_type", data.assignment_type)
      .order("created_at", { ascending: false })
      .limit(3);
    const priorSnippets = (priors ?? [])
      .map((p, i) => `PRIOR ATTEMPT ${i + 1}:\n${JSON.stringify(p.output).slice(0, 4000)}`)
      .join("\n\n");
    const uniquenessClause = priorSnippets
      ? `\n\nCRITICAL UNIQUENESS REQUIREMENT: The student has already generated outline(s) for this same work below. Your new outline MUST be substantively different — choose different claims, different angles, different evidence quotes when possible, different reasoning frames, different suggested phrases, and different wording throughout. Do not reuse sentences, phrasings, or structural choices from prior attempts.\n\n${priorSnippets}`
      : "";
    const variationNonce = Math.random().toString(36).slice(2, 10);

    const { text } = await generateText({
      model,
      system: systemPrompt + uniquenessClause,
      prompt: `Generate the framed outline now for: ${data.title}. Produce a fresh, original variant (variation token: ${variationNonce}). Return raw JSON only.`,
      temperature: 0.95,
    });

    const cleaned = text
      .replace(/^```json\s*/im, "")
      .replace(/^```\s*/im, "")
      .replace(/```\s*$/im, "")
      .trim();
    const jsonStart = cleaned.indexOf("{");
    const jsonEnd = cleaned.lastIndexOf("}");
    const jsonStr = jsonStart !== -1 && jsonEnd > jsonStart ? cleaned.slice(jsonStart, jsonEnd + 1) : cleaned;

    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      throw new Error("The AI response could not be parsed. Please try again.");
    }
    const output = OutlineSchema.parse(parsed);

    // Persist
    const { data: inserted, error: insertError } = await context.supabase
      .from("generations")
      .insert({
        user_id: context.userId,
        assignment_type: data.assignment_type,
        title: data.title,
        prompt: data.prompt ?? null,
        source_text: data.source_text ?? null,
        output: output as never,
      })
      .select("id")
      .single();
    if (insertError) throw new Error(insertError.message);

    if (!inserted) throw new Error("Failed to save generation.");
    return { id: inserted.id as string, output };

  });

const EssayDraftInput = z.object({
  title: z.string().min(1).max(300),
  prompt: z.string().max(10000).optional(),
  assignment_type: z.enum(["essay", "summary", "key_concepts"]),
  outline: OutlineSchema,
  format: z.enum(["MLA", "APA"]),
  level: z.enum(["intermediate", "advanced"]).default("intermediate"),
  tone: z.enum(["academic", "natural"]).default("academic"),
  word_min: z.number().int().min(100).max(10000).default(500),
  word_max: z.number().int().min(100).max(10000).default(800),
  author: z.string().max(200).optional(),
  voice_profile: VoiceProfileInput,
});

const EssayDraftSchema = z.object({
  title: z.string(),
  author_block: z.array(z.string()),
  paragraphs: z.array(z.string()),
  works_cited: z.array(z.string()),
});

export const generateEssayDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => EssayDraftInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const wmin2 = Math.min(data.word_min, data.word_max);
    const wmax2 = Math.max(data.word_min, data.word_max);
    const wordRange = { min: wmin2, max: wmax2 };

    const today = new Date().toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
    const formatRules =
      data.format === "MLA"
        ? `${citationFullRules("MLA")}\n\nFor THIS essay's author_block return EXACTLY these 4 lines as separate strings: ["Student Name", "Instructor Name", "Course Name", "${today}"].`
        : `${citationFullRules("APA")}\n\n${apa7AuthorBlockInstruction()}`;

    const levelClause =
      data.level === "advanced"
        ? "Advanced-level academic prose — precise vocabulary, varied sentence structure, analytical."
        : "Intermediate reading level — clear, direct sentences, common vocabulary.";
    const toneClause =
      data.tone === "natural"
        ? "Natural real-student voice — warm but academic, contractions OK, varied rhythm."
        : "Formal academic tone, no contractions.";

    const outlineJson = JSON.stringify(data.outline, null, 2);

    const system = `You are SmartEssay. Convert the student's CER outline into a FINISHED essay draft on "${data.title}".

${levelClause}
${toneClause}

LENGTH: ${wordRange.min}-${wordRange.max} words across exactly ${data.outline.sections.length} body paragraphs plus an intro and conclusion (so ${data.outline.sections.length + 2} paragraphs total).

CITATION FORMAT: ${data.format}. ${formatRules}

Rules:
- Use the EXACT quotes from the outline's evidence. Do not invent new quotes.
- Cite every quote inline using ${data.format} style, derived from the citation string in each evidence item.
- Fill in the reasoning blanks from each section yourself, drawing on the suggested options where helpful.
- Intro should set up the prompt and end with a clear thesis. Conclusion should echo the closing_frame.
- Output a single JSON object — no markdown.

QUOTE SPACING (CRITICAL): When a body paragraph contains 2+ cited quotes, NEVER place them back-to-back or in adjacent sentences. Between any two cited quotes you MUST write at least 2–3 full sentences of the student's own analysis, reasoning, transition, or commentary that interprets the first quote BEFORE introducing the next. Quotes should feel earned by the surrounding thought — never stacked like a list of evidence. A good rhythm per body paragraph is: topic/claim sentence → setup → quote 1 + citation → 2–3 sentences of reasoning/analysis that unpack quote 1 → transition sentence that pivots to the next angle → setup → quote 2 + citation → 2–3 more sentences of reasoning → closing/link sentence. Never have two sentences in a row each ending in a parenthetical citation.

ITALICS: Wherever the citation style requires italics (book titles, journal names, film/album titles, volume numbers in APA, long-work titles in MLA), wrap that text in single asterisks like *Title of Work*. Apply this in BOTH "paragraphs" (in-text mentions of titles) AND "works_cited" entries. Do NOT use asterisks for anything else (no bold, no emphasis). Article/chapter titles in quotation marks are NOT italicized.

Respond ONLY with JSON in this shape:
{
  "title": "string (the essay title)",
  "author_block": ["string", "..."],
  "paragraphs": ["intro paragraph", "body 1", "...", "conclusion"],
  "works_cited": ["entry 1", "entry 2"]
}

${styleAndVoiceRules(data.voice_profile as VoiceProfile | null | undefined)}

OUTLINE TO EXPAND:
${outlineJson}

${data.prompt ? `PROMPT THE ESSAY MUST ANSWER:\n"""\n${data.prompt}\n"""` : ""}`;

    const { fetchPriorVariants, buildUniquenessClause, logVariant } = await import("./variation");
    const priorDrafts = await fetchPriorVariants(
      context.supabase,
      context.userId,
      "essay_draft",
      data.title,
    );
    const { clause: draftUniqueness } = buildUniquenessClause(
      priorDrafts,
      "different opening, different sentence structures, different transitions, different word choice, and a different overall rhythm while still expanding the same outline faithfully",
    );

    const { text } = await generateText({
      model,
      system: system + draftUniqueness,
      prompt: `Write the finished ${data.format} essay now. Return raw JSON only.`,
      // Lower temperature when voice matching is on — high temperature pulls the
      // model away from the locked style profile and introduces variance.
      temperature: data.voice_profile ? 0.55 : 0.9,
    });

    const cleaned = text.replace(/^```json\s*/im, "").replace(/^```\s*/im, "").replace(/```\s*$/im, "").trim();
    const s = cleaned.indexOf("{");
    const e = cleaned.lastIndexOf("}");
    const jsonStr = s !== -1 && e > s ? cleaned.slice(s, e + 1) : cleaned;
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      throw new Error("The AI essay response could not be parsed. Please try again.");
    }
    const draft = EssayDraftSchema.parse(parsed);
    await logVariant(
      context.supabase,
      context.userId,
      "essay_draft",
      data.title,
      JSON.stringify({ paragraphs: draft.paragraphs.slice(0, 3) }),
    );
    return draft;
  });

// =====================================================================
// Peer-Reviewed Article Summary (APA 7)
// =====================================================================

const PeerReviewInput = z.object({
  title: z.string().min(1).max(500),
  source_text: z.string().max(80000).optional(),
  level: z.enum(["intermediate", "advanced"]).optional().default("advanced"),
  tone: z.enum(["academic", "natural"]).optional().default("academic"),
  evidence_count: z.number().int().min(1).max(10).optional().default(2),
  word_min: z.number().int().min(100).max(10000).optional().default(600),
  word_max: z.number().int().min(100).max(10000).optional().default(900),
  voice_profile: VoiceProfileInput,
});

const PeerReviewSchema = z.object({
  title: z.string(),
  author_block: z.array(z.string()),
  article_citation: z.string(),
  sections: z.object({
    introduction: z.string(),
    subjects: z.string(),
    instrument: z.string(),
    results: z.string(),
    critique: z.string(),
  }),
});

export type PeerReviewSummary = z.infer<typeof PeerReviewSchema>;

export const generatePeerReviewSummary = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => PeerReviewInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const levelClause =
      data.level === "intermediate"
        ? "Keep prose at an intermediate (10th-grade) reading level — clear, direct sentences."
        : "Use advanced academic prose — precise vocabulary, analytical phrasing.";

    const toneClause =
      data.tone === "natural"
        ? `VOICE & TONE: Write in a natural, real-student voice — warm but still academic. Mix short punchy sentences with longer flowing ones (high "burstiness"). Use contractions naturally (it's, don't, you're, can't, we've). Use real student transitions like "What's interesting is…", "This part really shows…", "At the same time…", "For example…", "Another thing that stands out…". Vary vocabulary and sentence starters — never repetitive or robotic. Smart, not overly formal or perfect.`
        : `VOICE & TONE: Clear, academic, and neutral. Avoid contractions and casual phrasing.`;

    const today = new Date().toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });

    const wmin = Math.min(data.word_min, data.word_max);
    const wmax = Math.max(data.word_min, data.word_max);
    const evidenceClause =
      data.evidence_count === 1
        ? "When you cite the article in the prose sections, include AT LEAST 1 direct or paraphrased piece of evidence (quote, statistic, or specific finding) per major claim, each with a narrative or parenthetical APA 7 citation."
        : `When you cite the article in the prose sections, include AT LEAST ${data.evidence_count} distinct pieces of evidence (direct quotes, statistics, or specific findings) per major claim across the introduction, results, and critique sections, each with a narrative or parenthetical APA 7 citation. Vary the angles (e.g. quantitative finding, author argument, methodological detail).`;

    const system = `You are SmartEssay. Summarize the supplied peer-reviewed article into a formal APA 7 (current edition, 7th) structured summary suitable for a college research methods course.

${levelClause}

${toneClause}

LENGTH TARGET: The full summary (all five sections combined, excluding the title page and references entry) should land between ${wmin} and ${wmax} words. Scale the depth of each section accordingly — do not pad with filler.

EVIDENCE DENSITY: ${evidenceClause}

OUTPUT — return ONLY raw JSON, no markdown, in this exact shape:
{
  "title": "Summary of: <article title in title case>",
  "author_block": ["Student Name", "Department of [Subject], Institution Name", "Course Number: Course Name", "Instructor Name", "${today}"],
  "article_citation": "Full APA 7 reference for the article (Author, A. A. (Year). Title of article in sentence case. Journal Name in Title Case, Volume(Issue), pages. https://doi.org/xxx)",
  "sections": {
    "introduction": "1–2 paragraphs: research problem, purpose of the study, research questions or hypotheses, and significance. Include narrative APA citations like Author (Year).",
    "subjects": "1 paragraph describing the participants/subjects: sample size, demographics, sampling method, inclusion/exclusion criteria, setting.",
    "instrument": "1–2 paragraphs describing the measurement instrument(s) and procedures used: name of instrument, what it measures, reliability/validity if reported, how data were collected.",
    "results": "1–2 paragraphs reporting the key findings: statistical results when given (e.g., p values, effect sizes, means/SDs), and what the authors concluded.",
    "critique": "1–2 paragraphs of YOUR critique: strengths, limitations (sample size, design, generalizability, bias), and suggestions for future research. Use hedged academic language."
  }
}

${APA7_RULES}

ADDITIONAL APA 7 RULES FOR THIS SUMMARY:
- Use narrative citations like "Smith (2020) found..." or parenthetical like "(Smith, 2020, p. 14)" inside the section prose where relevant.
- The article_citation must follow APA 7 reference format exactly as described in the rules above. Provide clean text; the renderer will italicize the journal title.
- Do NOT include section headings inside the section strings; the renderer adds them.
- Do NOT invent statistics not present in the article. If the article does not report something (e.g., reliability), say so.
- QUOTE SPACING: When a section contains 2+ citations, NEVER place them back-to-back or in adjacent sentences. Between any two citations, write at least 2–3 sentences of your own analysis, interpretation, or transition that unpacks the first finding before introducing the next. Never end two consecutive sentences with parenthetical citations.

${styleAndVoiceRules(data.voice_profile as VoiceProfile | null | undefined)}

ARTICLE TITLE THE STUDENT PROVIDED: "${data.title}"

${data.source_text ? `ARTICLE FULL TEXT:\n"""\n${data.source_text}\n"""` : "The student did not paste the full article text. Use the title to identify the well-known, peer-reviewed article and summarize it accurately. If you cannot identify the exact article, politely note the ambiguity and produce a general but well-structured APA 7 summary based on what the title suggests."}`;

    // Avoid repeating prior peer-review summaries for the same article+user
    const { data: priors } = await context.supabase
      .from("generations")
      .select("output")
      .eq("user_id", context.userId)
      .eq("title", data.title)
      .order("created_at", { ascending: false })
      .limit(3);
    const priorSnippets = (priors ?? [])
      .map((p, i) => `PRIOR ATTEMPT ${i + 1}:\n${JSON.stringify(p.output).slice(0, 4000)}`)
      .join("\n\n");
    const uniquenessClause = priorSnippets
      ? `\n\nCRITICAL UNIQUENESS REQUIREMENT: Prior summaries of this same article are below. Your new summary MUST be substantively different — different sentence structure, different word choice, different paraphrasings, different critique angles. Do not reuse sentences or phrasings from prior attempts.\n\n${priorSnippets}`
      : "";
    const variationNonce = Math.random().toString(36).slice(2, 10);

    const { text } = await generateText({
      model,
      system: system + uniquenessClause,
      prompt: `Produce the APA 7 peer-reviewed article summary now. Make it an original variant (variation token: ${variationNonce}). Return raw JSON only.`,
      temperature: data.voice_profile ? 0.55 : 0.9,
    });

    const cleaned = text.replace(/^```json\s*/im, "").replace(/^```\s*/im, "").replace(/```\s*$/im, "").trim();
    const s = cleaned.indexOf("{");
    const e = cleaned.lastIndexOf("}");
    const jsonStr = s !== -1 && e > s ? cleaned.slice(s, e + 1) : cleaned;
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      throw new Error("The AI response could not be parsed. Please try again.");
    }
    const output = PeerReviewSchema.parse(parsed);

    await context.supabase
      .from("generations")
      .insert({
        user_id: context.userId,
        assignment_type: "summary",
        title: data.title,
        prompt: "Peer-reviewed article APA 7 summary",
        source_text: data.source_text ?? null,
        output: output as never,
      });

    return output;
  });

// =====================================================================
// Essay Reviewer
// =====================================================================

const ReviewInput = z.object({
  title: z.string().max(300).optional(),
  essay_text: z.string().min(50, "Paste at least a few sentences of your essay.").max(60000),
  source_text: z.string().max(80000).optional(),
  citation_style: z.enum(["MLA", "APA"]).default("MLA"),
  prompt: z.string().max(10000).optional(),
  essay_type: z.string().max(80).optional(),
});

const ReviewSchema = z.object({
  overall_summary: z.string(),
  overall_score: z.number().min(0).max(100),
  thesis_assessment: z.string(),
  evidence_gaps: z.array(
    z.object({
      paragraph_index: z.number().int().min(1),
      excerpt: z.string(),
      issue: z.string(),
      suggested_quotes: z.array(
        z.object({
          quote: z.string(),
          citation: z.string(),
          why_it_fits: z.string(),
        })
      ),
    })
  ),
  logical_flow: z.object({
    overall: z.string(),
    paragraph_map: z.array(
      z.object({
        paragraph_index: z.number().int().min(1),
        role: z.string(),
        main_idea: z.string(),
        transition_in: z.string(),
        flow_score: z.enum(["strong", "ok", "weak"]),
      })
    ),
    flow_issues: z.array(
      z.object({
        between_paragraphs: z.string(),
        problem: z.string(),
        fix: z.string(),
      })
    ),
  }),
  improvement_ideas: z.array(
    z.object({
      priority: z.enum(["high", "medium", "low"]),
      category: z.string().min(1).max(40),
      suggestion: z.string(),
      example: z.string().optional(),
    })
  ),
  formatting_check: z
    .object({
      style: z.enum(["MLA", "APA"]),
      overall: z.string(),
      score: z.number().min(0).max(100),
      issues: z.array(
        z.object({
          category: z.string().min(1).max(60),
          severity: z.enum(["high", "medium", "low"]),
          description: z.string(),
          fix: z.string(),
        })
      ),
      strengths: z.array(z.string()).default([]),
    })
    .optional(),
});

export type EssayReview = z.infer<typeof ReviewSchema>;

export const reviewEssay = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ReviewInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const sourceClause = data.source_text
      ? `The student uploaded the SOURCE MATERIAL their essay analyzes. When you suggest quotes to add as evidence, you MUST pull them word-for-word from this source text only. Do NOT invent quotes. If you cannot find a fitting quote, say so and suggest the student paraphrase instead.\n\nSOURCE TEXT:\n"""\n${data.source_text}\n"""`
      : `The student did NOT upload source material. Do NOT invent or fabricate quotes. For "suggested_quotes", leave the array empty for every evidence gap, and in the "issue" field describe what kind of evidence is needed (e.g. "Needs a direct quote from chapter 3 supporting Daisy's materialism") so the student knows what to look up themselves. Focus the rest of the review (logical flow, improvement ideas) on what you CAN assess from the essay text alone.`;

    const promptClause = data.prompt
      ? `\n\nASSIGNMENT PROMPT THE ESSAY IS ANSWERING:\n"""\n${data.prompt}\n"""\nEvaluate how well the essay actually answers this prompt.`
      : "";

    const titleClause = data.title ? `ESSAY TITLE / TOPIC: "${data.title}"\n\n` : "";
    const essayTypeClause = data.essay_type
      ? `ESSAY TYPE: ${data.essay_type}. Tailor every part of the review (thesis expectations, evidence norms, structure, tone) to the conventions of this essay type.\n\n`
      : "";

    const citationRule = citationInlineRule(data.citation_style) + " " + citationReferenceRule(data.citation_style);

    const system = `You are SmartEssay's Essay Reviewer — a rigorous but kind academic coach. The student has submitted a draft. Your job is to:
1. Find places where claims need evidence and (if source material was uploaded) suggest REAL word-for-word quotes from that source to add, with proper citations.
2. Map the essay's logical flow paragraph-by-paragraph and flag weak transitions, contradictions, or jumps in reasoning.
3. Produce a prioritized list of concrete, actionable improvement ideas — never vague "make it stronger".
4. Check the draft against ${data.citation_style} formatting conventions (in-text citations, title formatting, Works Cited / References, block quotes, punctuation) and report what's right and what needs fixing.

${titleClause}${essayTypeClause}CITATION STYLE: ${data.citation_style}. ${citationRule}

${sourceClause}${promptClause}

STUDENT'S ESSAY DRAFT (paragraphs separated by blank lines):
"""
${data.essay_text}
"""

Number paragraphs starting at 1 (the first non-empty paragraph is 1).

Rules:
- Be honest. If the essay is strong, say so — do not invent problems.
- Every "evidence_gap" must point at an actual claim in the draft that lacks support, with the excerpt copied verbatim from the essay (truncated to ~200 chars).
- Suggested quotes must be word-for-word from the uploaded source text AND must directly support the specific claim in that paragraph. Never fabricate. If no source was uploaded, suggested_quotes MUST be an empty array. If a source IS uploaded but no quote in it strongly supports the claim, return an empty suggested_quotes array and say so in "issue".

${STRICT_EVIDENCE_RULE}

- "flow_score" values are exactly: "strong", "ok", or "weak".
- "priority" values are exactly: "high", "medium", "low".
- "category" values are exactly one of: "thesis", "evidence", "reasoning", "flow", "structure", "style", "counterargument", "citation", "grammar".
- overall_score is 0–100 — rough holistic quality for the student's grade level.
- Aim for 2–6 evidence_gaps, 3–8 improvement_ideas, and a paragraph_map covering every body paragraph.

FORMATTING CHECK (${data.citation_style}):
Evaluate the draft against ${data.citation_style} conventions and fill out "formatting_check". Use what is visible in the draft text — don't penalize the student for things only the exported PDF controls (margins, font, double-spacing) unless they are clearly wrong inline.
Check things like: in-text citation format (${data.citation_style === "MLA" ? "e.g. (Fitzgerald 23) — author + page, no comma" : "e.g. (Fitzgerald, 1925, p. 23) — author, year, p. page"}), punctuation around citations (period AFTER the parenthetical), block-quote handling, title formatting (italics for novels/films, quotation marks for short works, no underlining), capitalization (${data.citation_style === "MLA" ? "MLA title case" : "APA sentence case for article titles in references"}), use of "et al.", placement of Works Cited / References, hanging indent in references if visible, consistent author-name format, and any inline reference-list entries.
- "issues[].category" should be short (e.g. "In-text citation", "Title formatting", "Works Cited entry", "Block quote", "Punctuation").
- "issues[].severity" is exactly "high", "medium", or "low".
- "score" is 0–100 strictly for ${data.citation_style} formatting compliance — separate from overall_score.
- "strengths" lists 0–4 things the student already gets right in ${data.citation_style}.
- If the draft has no visible citations at all, set score appropriately and add an issue noting that no in-text citations were found.

Respond ONLY with raw JSON (no markdown, no commentary) in this exact shape:
{
  "overall_summary": "2–3 sentence honest summary of the draft's current state.",
  "overall_score": 0,
  "thesis_assessment": "1–2 sentences on the thesis: is it clear, arguable, and carried through the essay?",
  "evidence_gaps": [
    {
      "paragraph_index": 1,
      "excerpt": "...",
      "issue": "...",
      "suggested_quotes": [
        { "quote": "...", "citation": "...", "why_it_fits": "..." }
      ]
    }
  ],
  "logical_flow": {
    "overall": "1–2 sentences on the essay's overall flow and structure.",
    "paragraph_map": [
      {
        "paragraph_index": 1,
        "role": "intro | body | counterargument | conclusion | etc.",
        "main_idea": "...",
        "transition_in": "How this paragraph connects to the previous one (or 'opening' for intro).",
        "flow_score": "strong"
      }
    ],
    "flow_issues": [
      { "between_paragraphs": "2 → 3", "problem": "...", "fix": "..." }
    ]
  },
  "improvement_ideas": [
    { "priority": "high", "category": "thesis", "suggestion": "...", "example": "..." }
  ],
  "formatting_check": {
    "style": "${data.citation_style}",
    "overall": "1–2 sentence summary of how well the draft follows ${data.citation_style} conventions.",
    "score": 0,
    "issues": [
      { "category": "In-text citation", "severity": "high", "description": "...", "fix": "..." }
    ],
    "strengths": ["..."]
  }
}`;

    const { text } = await generateText({
      model,
      system,
      prompt: `Review the essay now. Return raw JSON only.`,
      temperature: 0.4,
    });

    const cleaned = text.replace(/^```json\s*/im, "").replace(/^```\s*/im, "").replace(/```\s*$/im, "").trim();
    const s = cleaned.indexOf("{");
    const e = cleaned.lastIndexOf("}");
    const jsonStr = s !== -1 && e > s ? cleaned.slice(s, e + 1) : cleaned;
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      throw new Error("The AI review could not be parsed. Please try again.");
    }
    const output = ReviewSchema.parse(parsed);

    const { data: inserted, error: insertError } = await context.supabase
      .from("generations")
      .insert({
        user_id: context.userId,
        assignment_type: "review",
        title: data.title?.trim() || "Essay review",
        prompt: data.prompt ?? null,
        source_text: data.source_text ?? null,
        output: output as never,
      })
      .select("id")
      .single();
    if (insertError) throw new Error(insertError.message);
    if (!inserted) throw new Error("Failed to save review.");

    return { id: inserted.id as string, output };
  });


// =====================================================================
// Suggest quotes from a known work (AI memory, no uploaded source)
// =====================================================================

const SuggestQuotesInput = z.object({
  title: z.string().min(1).max(300),
  citation_style: z.enum(["MLA", "APA"]).default("MLA"),
  gaps: z
    .array(
      z.object({
        paragraph_index: z.number().int().min(1),
        excerpt: z.string().max(2000),
        issue: z.string().max(2000),
      })
    )
    .min(1)
    .max(20),
});

const SuggestQuotesSchema = z.object({
  results: z.array(
    z.object({
      paragraph_index: z.number().int().min(1),
      quotes: z.array(
        z.object({
          quote: z.string(),
          citation: z.string(),
          why_it_fits: z.string(),
          confidence: z.enum(["high", "medium", "low"]).default("medium"),
        })
      ),
    })
  ),
});

export type SuggestedQuotes = z.infer<typeof SuggestQuotesSchema>;

export const suggestQuotesForGaps = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SuggestQuotesInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const citationRule = citationInlineRule(data.citation_style);

    const gapsJson = JSON.stringify(data.gaps, null, 2);

    const system = `You are SmartEssay's quote suggester. The student is analyzing the work titled "${data.title}". For each evidence gap below, suggest 1–3 real, widely-known, verifiable word-for-word quotes from that work that would fill the gap.

CITATION STYLE: ${data.citation_style}. ${citationRule}

STRICT RULES:
- ONLY suggest quotes you are highly confident are word-for-word accurate from "${data.title}" AND that DIRECTLY support the specific claim in the gap's "issue"/"excerpt". If you are not confident OR no quote truly supports the claim, return an empty quotes array for that gap — DO NOT invent, guess, or stretch a quote to fit.
- Set "confidence": "high" only for famous, widely-quoted lines you are certain of AND that clearly back the claim. Use "medium" for likely-accurate passages with strong relevance, and "low" sparingly — never use "low" for off-topic content.
- If "${data.title}" does not appear to be a real, identifiable work you know well, return an empty quotes array for every gap.
- Never fabricate page numbers. If unsure of a page, omit it from the citation.
- The "why_it_fits" field MUST name exactly how the quote supports the claim in 1–2 student-friendly sentences. If you can't write a clear "why_it_fits", the quote isn't a real match — drop it.

${STRICT_EVIDENCE_RULE}

EVIDENCE GAPS (from the student's draft):
${gapsJson}

Respond ONLY with raw JSON in this exact shape:
{
  "results": [
    {
      "paragraph_index": 1,
      "quotes": [
        { "quote": "...", "citation": "...", "why_it_fits": "...", "confidence": "high" }
      ]
    }
  ]
}

Include one result entry per input gap, in the same order, even if quotes is an empty array.`;

    const { text } = await generateText({
      model,
      system,
      prompt: `Suggest quotes now. Return raw JSON only.`,
      temperature: 0.3,
    });

    const cleaned = text.replace(/^```json\s*/im, "").replace(/^```\s*/im, "").replace(/```\s*$/im, "").trim();
    const s = cleaned.indexOf("{");
    const e = cleaned.lastIndexOf("}");
    const jsonStr = s !== -1 && e > s ? cleaned.slice(s, e + 1) : cleaned;
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      throw new Error("The AI quote suggestions could not be parsed. Please try again.");
    }
    return SuggestQuotesSchema.parse(parsed);
  });


// ============================================================
// Title resolution — verify the work the student typed BEFORE
// generating an outline. Catches typos like "I am the Mountain
// Manzanar" being silently treated as "Farewell to Manzanar".
// ============================================================
const ResolveTitleInput = z.object({
  title: z.string().min(1).max(300),
});

const ResolveTitleSchema = z.object({
  status: z.enum(["match", "suggest", "unknown"]),
  canonical_title: z.string().max(300).optional().nullable(),
  author: z.string().max(200).optional().nullable(),
  work_type: z.string().max(80).optional().nullable(),
  confidence: z.enum(["high", "medium", "low"]),
  note: z.string().max(400).optional().nullable(),
});

export type ResolveTitleResult = z.infer<typeof ResolveTitleSchema>;

export const resolveTitle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ResolveTitleInput.parse(input))
  .handler(async ({ data }) => {
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const system = `You verify the title of a literary, academic, or media work that a student typed in. The student may have typos, partial titles, or misremembered words. Your job is to figure out the SINGLE most likely real work they mean — or admit you do not know.

STRICT RULES:
- Only return a canonical_title for a REAL, well-known, identifiable work (book, play, poem, film, article, etc.) you are confident about.
- Do NOT silently swap to a different work just because part of the title matches. "I am the Mountain Manzanar" is NOT "Farewell to Manzanar" — they are different works (or one may not exist). When the student's input could plausibly be its own real work, prefer status "unknown" over a wrong substitution.
- Compare the student input to the canonical title token-by-token. If MORE THAN ONE meaningful word differs or is missing, set status to "suggest" (not "match") so the student can confirm.
- If you cannot identify a real work that fits, set status to "unknown".

Return ONLY raw JSON in this exact shape:
{
  "status": "match" | "suggest" | "unknown",
  "canonical_title": "string or null",
  "author": "string or null",
  "work_type": "novel | memoir | play | poem | short story | film | article | essay | other | null",
  "confidence": "high" | "medium" | "low",
  "note": "one short sentence the student will see explaining the match, suggestion, or why it's unknown"
}

STATUS GUIDE:
- "match": canonical_title is essentially what the student typed (allowing for capitalization, articles like "The", minor typos in 1 word). confidence should be "high".
- "suggest": you have a strong guess but the student's input differs meaningfully (extra/missing words, different phrasing). Always use this when in doubt rather than "match".
- "unknown": no confident identification. canonical_title MUST be null.`;

    const { text } = await generateText({
      model,
      system,
      prompt: `Student typed this title: """${data.title}"""\n\nIdentify the work. Return raw JSON only.`,
      temperature: 0.1,
    });

    const cleaned = text.replace(/^```json\s*/im, "").replace(/^```\s*/im, "").replace(/```\s*$/im, "").trim();
    const s = cleaned.indexOf("{");
    const e = cleaned.lastIndexOf("}");
    const jsonStr = s !== -1 && e > s ? cleaned.slice(s, e + 1) : cleaned;
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      return { status: "unknown" as const, confidence: "low" as const, canonical_title: null, author: null, work_type: null, note: "Couldn't verify this title automatically." };
    }
    const result = ResolveTitleSchema.parse(parsed);
    if (result.status !== "unknown" && result.canonical_title) {
      const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]/g, "").replace(/\b(the|a|an)\b/g, "").replace(/\s+/g, " ").trim();
      const a = norm(data.title);
      const b = norm(result.canonical_title);
      if (a && b && a !== b) {
        const aw = new Set(a.split(" "));
        const bw = new Set(b.split(" "));
        let diff = 0;
        aw.forEach((w) => { if (!bw.has(w)) diff++; });
        bw.forEach((w) => { if (!aw.has(w)) diff++; });
        if (diff > 1 && result.status === "match") {
          return { ...result, status: "suggest" as const };
        }
      }
    }
    return result;
  });



// =====================================================================
// Essay -> Outline (reverse engineer a draft into intro/body/conclusion)
// =====================================================================

const EssayToOutlineInput = z.object({
  title: z.string().max(300).optional(),
  essay_text: z.string().min(50, "Paste at least a few sentences of your essay.").max(60000),
  prompt: z.string().max(10000).optional(),
});

const EssayToOutlineSchema = z.object({
  thesis: z.string(),
  introduction: z.object({
    hook: z.string(),
    context: z.string(),
    thesis_statement: z.string(),
  }),
  body_paragraphs: z.array(
    z.object({
      paragraph_index: z.number().int().min(1),
      topic_sentence: z.string(),
      main_points: z.array(z.string()),
      evidence: z.array(z.string()),
      link_to_thesis: z.string(),
    })
  ),
  conclusion: z.object({
    restated_thesis: z.string(),
    synthesis: z.string(),
    closing_thought: z.string(),
  }),
  notes: z.array(z.string()).default([]),
});

export type EssayToOutline = z.infer<typeof EssayToOutlineSchema>;

export const outlineEssay = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => EssayToOutlineInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const titleClause = data.title ? `ESSAY TITLE / TOPIC: "${data.title}"\n\n` : "";
    const promptClause = data.prompt
      ? `\n\nASSIGNMENT PROMPT THE ESSAY IS ANSWERING:\n"""\n${data.prompt}\n"""\n`
      : "";

    const system = `You are SmartEssay's Outline Extractor. The student has written an essay draft. Your job is to REVERSE-ENGINEER it into a clean structural outline so the student can see the bones of their own argument: introduction, body paragraphs, and conclusion.

${titleClause}${promptClause}

STUDENT'S ESSAY DRAFT (paragraphs separated by blank lines):
"""
${data.essay_text}
"""

Number body paragraphs starting at 1. Identify the intro and conclusion separately from body paragraphs. If the draft is missing one of these sections, still fill the field but flag it briefly in "notes".

Rules:
- Pull the thesis VERBATIM if it appears in the draft; if implied, paraphrase it concisely.
- For each body paragraph, identify the topic sentence (verbatim or paraphrased), 2–4 main supporting points the paragraph actually makes, any evidence the student uses (quotes, statistics, examples — short paraphrases are fine), and a 1-sentence note on how it links to the thesis.
- For the "evidence" array: ONLY list evidence the student ALREADY used in the draft that genuinely supports the topic sentence. Do not invent, suggest, or add evidence that isn't in the draft. If a paragraph's evidence doesn't actually back its claim, leave evidence empty and flag it in "notes".
- Keep everything concise and skimmable. This is an outline, not a rewrite.
- "notes" is a short list (0–4 items) of structural observations: missing intro/conclusion, paragraphs without a clear topic sentence, body paragraphs that don't support the thesis, evidence that doesn't actually support its claim, etc.

${STRICT_EVIDENCE_RULE}

Respond ONLY with raw JSON (no markdown) in this exact shape:
{
  "thesis": "...",
  "introduction": {
    "hook": "...",
    "context": "...",
    "thesis_statement": "..."
  },
  "body_paragraphs": [
    {
      "paragraph_index": 1,
      "topic_sentence": "...",
      "main_points": ["...", "..."],
      "evidence": ["..."],
      "link_to_thesis": "..."
    }
  ],
  "conclusion": {
    "restated_thesis": "...",
    "synthesis": "...",
    "closing_thought": "..."
  },
  "notes": ["..."]
}`;

    const { text } = await generateText({
      model,
      system,
      prompt: `Extract the outline now. Return raw JSON only.`,
      temperature: 0.3,
    });

    const cleaned = text.replace(/^```json\s*/im, "").replace(/^```\s*/im, "").replace(/```\s*$/im, "").trim();
    const s = cleaned.indexOf("{");
    const e = cleaned.lastIndexOf("}");
    const jsonStr = s !== -1 && e > s ? cleaned.slice(s, e + 1) : cleaned;
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      throw new Error("The AI outline could not be parsed. Please try again.");
    }
    const output = EssayToOutlineSchema.parse(parsed);

    const { data: inserted, error: insertError } = await context.supabase
      .from("generations")
      .insert({
        user_id: context.userId,
        assignment_type: "review",
        title: data.title?.trim() || "Essay outline",
        prompt: data.prompt ?? null,
        source_text: null,
        output: output as never,
      })
      .select("id")
      .single();
    if (insertError) throw new Error(insertError.message);
    if (!inserted) throw new Error("Failed to save outline.");

    return { id: inserted.id as string, output };
  });

// =====================================================================
// Pull evidence for ONE specific claim (claim-aware, strict alignment)
// =====================================================================

const PullEvidenceInput = z.object({
  claim: z.string().min(3).max(2000),
  title: z.string().min(1).max(300).optional(),
  source_text: z.string().max(80000).optional(),
  citation_style: z.enum(["MLA", "APA"]).default("MLA"),
  max_quotes: z.number().int().min(1).max(5).optional().default(3),
});

const PullEvidenceSchema = z.object({
  claim: z.string(),
  source_mode: z.enum(["uploaded_source", "ai_memory", "none"]),
  quotes: z.array(
    z.object({
      quote: z.string(),
      citation: z.string(),
      why_supports: z.string(),
      confidence: z.enum(["high", "medium", "low"]).default("medium"),
      match_strength: z.enum(["strong", "moderate"]).default("strong"),
    })
  ),
  no_match_message: z.string().optional().nullable(),
  revision_suggestion: z.string().optional().nullable(),
});

export type PullEvidenceResult = z.infer<typeof PullEvidenceSchema>;

export const pullEvidenceForClaim = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => PullEvidenceInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const citationRule = citationInlineRule(data.citation_style);
    const sourceMode: "uploaded_source" | "ai_memory" | "none" = data.source_text
      ? "uploaded_source"
      : data.title
        ? "ai_memory"
        : "none";

    const sourceClause = data.source_text
      ? `SOURCE TEXT (the only place quotes may come from):\n"""\n${data.source_text}\n"""`
      : data.title
        ? `No source text was uploaded. You may suggest real, widely-known, word-for-word quotes from the work titled "${data.title}" — but ONLY ones you are highly confident are accurate AND that directly support the claim. If you don't know the work well, return zero quotes.`
        : `No source text and no work title were provided. Return zero quotes and explain in no_match_message that the student needs to add a source or work title.`;

    const system = `You are SmartEssay's per-claim evidence puller. You pull supporting evidence for ONE specific claim at a time.

CLAIM TO SUPPORT:
"""
${data.claim}
"""

CITATION STYLE: ${data.citation_style}. ${citationRule}

${sourceClause}

${STRICT_EVIDENCE_RULE}

Process:
1. Silently restate the claim in your own words.
2. Scan the source/work for passages that DIRECTLY support that restated claim.
3. Keep ONLY passages that pass the strict alignment rule above. Discard anything tangential, loosely related, or merely topical.
4. Return at most ${data.max_quotes} quotes — fewer is better. If nothing strongly supports the claim, return zero.
5. For each kept quote, write a 1–2 sentence student-friendly "why_supports" naming the exact link to the claim.
6. If you return zero quotes, fill "no_match_message" with a clear student-facing explanation, and "revision_suggestion" with a concrete next step (revise the claim to match what the source actually says, OR look for stronger sources on X).

"match_strength" guidance:
- "strong" = the passage explicitly states or demonstrates the claim.
- "moderate" = the passage clearly implies the claim but the student will need 1 short sentence of reasoning to connect it. Use sparingly.
- Anything weaker than "moderate" should NOT be returned at all.

Respond ONLY with raw JSON (no markdown) in this exact shape:
{
  "claim": "${data.claim.replace(/"/g, '\\"')}",
  "source_mode": "${sourceMode}",
  "quotes": [
    { "quote": "...", "citation": "...", "why_supports": "...", "confidence": "high", "match_strength": "strong" }
  ],
  "no_match_message": "string or null — required if quotes is empty",
  "revision_suggestion": "string or null — required if quotes is empty"
}`;

    const { text } = await generateText({
      model,
      system,
      prompt: `Pull supporting evidence for the claim now. Return raw JSON only.`,
      temperature: 0.2,
    });

    const cleaned = text.replace(/^```json\s*/im, "").replace(/^```\s*/im, "").replace(/```\s*$/im, "").trim();
    const s = cleaned.indexOf("{");
    const e = cleaned.lastIndexOf("}");
    const jsonStr = s !== -1 && e > s ? cleaned.slice(s, e + 1) : cleaned;
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      throw new Error("The AI evidence response could not be parsed. Please try again.");
    }
    const output = PullEvidenceSchema.parse(parsed);
    // Safety: if no quotes returned, ensure a no_match_message exists.
    if (output.quotes.length === 0 && !output.no_match_message) {
      output.no_match_message =
        "No strong, directly-supporting evidence was found for this claim in the available source. Consider revising the claim or finding stronger sources.";
    }
    return output;
  });

